import React from "react";
import { MapPin, Shirt, Building2 } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";

const MAPS_URL = "https://www.google.com/maps/dir/?api=1&destination=" +
  encodeURIComponent("The Metropolitan Club, 5895 Windward Parkway, Alpharetta, GA 30005");

export default function WeddingDetails() {
  const { content } = useContent();
  const schedule = content?.details?.schedule || [];
  const scheduleTbd = content?.details?.schedule_tbd;
  const scheduleNote = content?.details?.schedule_note;
  const dress = content?.details?.dress_code;
  const attireNote = content?.details?.attire_note;
  const attireHer = content?.details?.attire_her_image;
  const attireHim = content?.details?.attire_him_image;
  const attire = content?.details?.attire_examples || [];

  return (
    <div data-testid="wedding-details-page">
      <PageHero overline="Join Us" title="Wedding Details" subtitle="Saturday, February 20, 2027" />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 space-y-20">
        {/* venue */}
        <Reveal>
          <div className="stone-panel border border-taupe/40 rounded-lg p-10 sm:p-14 text-center">
            <Building2 className="mx-auto text-olive" size={30} />
            <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-matte">The Metropolitan Club</h2>
            <p className="mt-4 text-taupe text-lg">5895 Windward Parkway<br />Alpharetta, Georgia 30005</p>
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer"
              className="btn-olive rounded-full px-8 py-3.5 overline inline-flex items-center gap-2 mt-8" data-testid="directions-btn">
              <MapPin size={16} /> Get Directions
            </a>
            <p className="mt-8 text-sm text-taupe/90 max-w-lg mx-auto">
              The ceremony, cocktail hour, and reception will all take place <strong className="text-olive">indoors at the same venue.</strong>
            </p>
          </div>
        </Reveal>

        {/* schedule */}
        <Reveal>
          <div>
            <div className="text-center mb-12">
              <p className="overline text-olive">The Timeline</p>
              <h2 className="font-serif text-4xl sm:text-5xl mt-2 text-matte">Wedding Day Schedule</h2>
            </div>
            {scheduleTbd ? (
              <div className="max-w-2xl mx-auto text-center bg-soft-white border border-taupe/40 rounded-lg p-12" data-testid="schedule-tbd">
                <p className="font-serif text-5xl text-gold tracking-wide">TBD</p>
                <p className="mt-5 text-taupe text-lg">{scheduleNote}</p>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto">
                {schedule.map((it, i) => (
                  <div key={i} className="flex items-center gap-6 py-6 border-b border-taupe/30" data-testid={`schedule-item-${i}`}>
                    <div className="font-serif text-2xl text-olive w-28 shrink-0">{it.time}</div>
                    <div className="gold-line shrink-0" />
                    <div className="font-body text-lg text-matte">{it.title}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Reveal>

        {/* dress code */}
        <Reveal>
          <div className="bg-olive text-soft-white rounded-lg p-10 sm:p-14 text-center">
            <Shirt className="mx-auto text-gold" size={28} />
            <h2 className="font-serif text-4xl mt-4">Elevated Cocktail Attire</h2>
            <p className="mt-4 max-w-xl mx-auto text-soft-white/85 text-lg">{dress}</p>
            {attireNote && (
              <p className="mt-6 inline-block bg-gold text-matte rounded-full px-6 py-2 overline" data-testid="attire-note">{attireNote}</p>
            )}
          </div>
        </Reveal>

        {/* attire examples */}
        {(attireHer || attireHim || attire.length > 0) && (
          <Reveal>
            <div>
              <div className="text-center mb-10">
                <p className="overline text-olive">A Little Inspiration</p>
                <h2 className="font-serif text-4xl sm:text-5xl mt-2 text-matte">Attire Examples</h2>
              </div>
              {attireHer && (
                <figure className="mb-10">
                  <div className="overflow-hidden rounded-lg border border-taupe/40">
                    <img src={attireHer} alt="Elevated cocktail attire examples for her" loading="lazy" className="w-full h-auto" data-testid="attire-her-image" />
                  </div>
                  <figcaption className="text-center overline text-olive mt-4">For Her</figcaption>
                </figure>
              )}
              {attireHim && (
                <figure className="mb-10">
                  <div className="overflow-hidden rounded-lg border border-taupe/40">
                    <img src={attireHim} alt="Elevated cocktail attire examples for him" loading="lazy" className="w-full h-auto" data-testid="attire-him-image" />
                  </div>
                  <figcaption className="text-center overline text-olive mt-4">For Him</figcaption>
                </figure>
              )}
              {attire.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-8 max-w-3xl mx-auto justify-center">
                  {attire.map((a, i) => (
                    <div key={i} className="bg-soft-white border border-taupe/40 rounded-lg overflow-hidden sm:col-start-1 sm:col-end-3 sm:max-w-sm sm:mx-auto w-full" data-testid={`attire-example-${i}`}>
                      <div className="h-96 overflow-hidden bg-taupe-light">
                        {a.image && <img src={a.image} alt={`${a.label} attire example`} loading="lazy" className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700" />}
                      </div>
                      <div className="p-6 text-center">
                        <p className="overline text-olive">{a.label}</p>
                        <p className="text-taupe mt-2">{a.note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
